import { Product, ToastProvider } from '@cint/wonderment';
import { LucidiumProvider } from '@lucidhq/lucidium';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router-dom';

import { Analytics } from '@/analytics';
import { isMaintenanceModeError } from '@/api/httpClient';
import { AuthProvider } from '@/auth';
import { ErrorBoundary } from '@/components';
import '@/global.css';
import { useMaintenanceMode } from '@/hooks';
// Imported eagerly rather than lazily: this is the page the app falls back to
// when things are broken, so it must not depend on fetching another chunk.
import { Maintenance } from '@/pages/Maintenance';
import { theme } from '@/theme/theme';
import { SCOPES } from '@/utils/permissions';

import { App } from './App';
import { Appcues } from './Appcues';
import { IncentivesContextProvider } from './context/IncentivesContext';
import { NavigationContextProvider } from './context/NavigationContext';
import { PermissionsProvider } from './context/PermissionsContext';
import { SplitProvider } from './splitIO';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) =>
        !isMaintenanceModeError(error as AxiosError) && failureCount < 1,
    },
  },
});

const AuthenticatedApp = () => (
  <QueryClientProvider client={queryClient}>
    <NavigationContextProvider>
      <PermissionsProvider>
        <Analytics />
        <Appcues />
        <IncentivesContextProvider>
          <ToastProvider>
            <App />
          </ToastProvider>
        </IncentivesContextProvider>
      </PermissionsProvider>
    </NavigationContextProvider>
  </QueryClientProvider>
);

/**
 * Maintenance mode takes over the whole viewport without changing the URL: it
 * renders inside auth, but above the navigation and data providers that are
 * useless while the BFF is down. Keeping the URL means a reload retries the
 * page the user was actually on.
 */
const AppContent = () => {
  const isMaintenanceMode = useMaintenanceMode();

  return isMaintenanceMode ? <Maintenance /> : <AuthenticatedApp />;
};

const AppProviders = () => (
  <SplitProvider>
    <LucidiumProvider theme={theme}>
      <Product.Engage>
        <ErrorBoundary>
          <AuthProvider scopes={Object.values(SCOPES)}>
            <AppContent />
          </AuthProvider>
        </ErrorBoundary>
      </Product.Engage>
    </LucidiumProvider>
  </SplitProvider>
);

const router = createBrowserRouter(
  createRoutesFromElements(<Route path="*" element={<AppProviders />} />)
);

export const AppWrapper = () => <RouterProvider router={router} />;
